import { useEffect } from "react";
import {
  isNativeKeyTarget,
  resolveVideoKeyAction,
} from "@/components/video/controls/keymap";
import { getNextChartHeight } from "@/utils/chartHeight";

export interface ChartControls {
  zoomIn: () => void;
  zoomOut: () => void;
  scrollLeft: () => void;
  scrollRight: () => void;
  centerOnTime: (time: number) => void;
}

export interface VideoControls {
  togglePlayPause: () => void;
  // Unconditional pause (never plays). Used when leaving the page so the
  // media's update stream stops immediately.
  pause: () => void;
  // Optional so payloads that predate issue #1206 remain type-valid; the
  // keyboard handlers no-op when they are absent.
  toggleMute?: () => void;
  toggleFullscreen?: () => void;
}

interface UseAnalyticsKeyboardShortcutsArgs {
  chartControls: ChartControls | null;
  videoControls: VideoControls | null;
  seekSharedTimeline: (deltaSeconds: number) => void;
  chartHeight: number;
  onChartHeightChange: (value: number) => void;
  showNotesPanel: boolean;
  isNoteModalOpen: boolean;
  onCloseNotesPanel: () => void;
  onCloseNoteModal: () => void;
}

/**
 * Global keyboard shortcuts for the analytics page.
 *
 * Registers two listeners: a window keydown that closes the notes panel or
 * note modal on Escape, and a document keydown handling transport via the
 * shared video keymap (p/Space, arrows, m, f — routed to the telemetry
 * timeline and the video element) plus the analytics-only chart keys
 * (J/K scroll, +/- zoom, I/O height). Shortcuts are skipped while the user
 * is typing in a native key target (input, textarea, select, or
 * contentEditable element).
 */
export function useAnalyticsKeyboardShortcuts({
  chartControls,
  videoControls,
  seekSharedTimeline,
  chartHeight,
  onChartHeightChange,
  showNotesPanel,
  isNoteModalOpen,
  onCloseNotesPanel,
  onCloseNoteModal,
}: UseAnalyticsKeyboardShortcutsArgs): void {
  // Close notes panel with Escape key
  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        if (showNotesPanel) {
          onCloseNotesPanel();
        } else if (isNoteModalOpen) {
          onCloseNoteModal();
        }
      }
    };

    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [showNotesPanel, isNoteModalOpen, onCloseNotesPanel, onCloseNoteModal]);

  // Keyboard navigation
  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      // A handler closer to the target (e.g. the timeline lane slider's own
      // arrow handling) already claimed this keystroke.
      if (event.defaultPrevented) {
        return;
      }

      // Prevent keyboard navigation when user is typing in inputs
      if (isNativeKeyTarget(document.activeElement)) {
        return;
      }

      const { key } = event;
      const action = resolveVideoKeyAction(event);

      if (action !== null) {
        event.preventDefault();
        switch (action) {
          case "play-pause":
            videoControls?.togglePlayPause();
            break;
          case "seek-back":
            seekSharedTimeline(-2);
            break;
          case "seek-forward":
            seekSharedTimeline(2);
            break;
          case "seek-back-large":
            seekSharedTimeline(-10);
            break;
          case "seek-forward-large":
            seekSharedTimeline(10);
            break;
          case "toggle-mute":
            videoControls?.toggleMute?.();
            break;
          case "toggle-fullscreen":
            videoControls?.toggleFullscreen?.();
            break;
        }
        return;
      }

      // Analytics-only chart keys, handled outside the shared video keymap.
      // j/k scroll the chart; k is free for that because play/pause lives on
      // p/Space.
      switch (key) {
        case "j":
        case "J":
          event.preventDefault();
          if (chartControls) {
            // j: Scroll chart left
            chartControls.scrollLeft();
          }
          break;

        case "k":
        case "K":
          event.preventDefault();
          if (chartControls) {
            // k: Scroll chart right
            chartControls.scrollRight();
          }
          break;

        case "-":
        case "_":
          event.preventDefault();
          if (chartControls) {
            chartControls.zoomOut();
          }
          break;

        case "=":
        case "+":
          event.preventDefault();
          if (chartControls) {
            chartControls.zoomIn();
          }
          break;

        case "i":
        case "I":
          event.preventDefault();
          // i: Make chart shorter
          if (chartHeight > 120) {
            const newHeight = getNextChartHeight(chartHeight, "decrease");
            onChartHeightChange(newHeight);
          }
          break;

        case "o":
        case "O":
          event.preventDefault();
          // o: Make chart taller
          if (chartHeight < 300) {
            const newHeight = getNextChartHeight(chartHeight, "increase");
            onChartHeightChange(newHeight);
          }
          break;
      }
    };

    document.addEventListener("keydown", handleKeyDown);
    return () => document.removeEventListener("keydown", handleKeyDown);
  }, [
    chartControls,
    videoControls,
    seekSharedTimeline,
    onChartHeightChange,
    chartHeight,
  ]);
}
